import React from 'react';

import { FmSpinner } from '@/components/ui/FmFeedback';

/**
 * The Suspense boundary a code-split SLOT registration owns.
 *
 * The registration has to own its own boundary. A bare
 * `React.lazy` renderer suspends to whatever boundary happens to be above it,
 * and for almost every consumer that is the route-level
 * `<Suspense fallback={<RouteSpinner/>}>` in App.tsx covering the whole
 * `<Routes>` tree. React hides a suspended boundary's host children with
 * `display:none`, so one late map chunk blanked the entire application: an
 * owner with the plan selector open lost DOM focus, the list closed under
 * them, and nothing reopened it.
 *
 * That boundary was introduced with `fallback={null}`, on the reasoning that
 * the hosting region already owns its frame. It does own the frame -- and the
 * inner boundary is nearer, so it PREEMPTS the consumer's own fallback and the
 * frame was left carrying nothing at all. Every map opened behind a blank
 * canvas, and a report workflow opened as an empty modal.
 *
 * So the default is a real, owner-visible loading affordance, marked
 * `data-slot-loading="<key>"` so the state is observable from the public
 * boundary instead of being indistinguishable from an empty frame. A consumer
 * that owns a better one passes it as `fallback`.
 */
export function lazySlotFallback(slotKey: string): React.ReactElement {
  return <FmSpinner data-slot-loading={slotKey} />;
}

export interface LazySlotModule<Props extends object> {
  default: React.ComponentType<Props>;
}

/**
 * What a registration puts in its boundary while the chunk is in flight.
 *
 * A plain node covers a slot that is always on screen once mounted: a map fills
 * its region whenever the region exists, so "pending" and "the owner is waiting
 * for this" are the same state. A RESOLVER covers a slot that is mounted while
 * CLOSED -- a report workflow renders its own `open={false}` modal, so it is in
 * the tree on every report page whether or not the owner ever asked for it. A
 * fixed node there paints an affordance for something nobody is waiting on.
 */
export type LazySlotFallback<Props extends object> =
  | React.ReactNode
  | ((props: Props) => React.ReactNode);

/**
 * Build the renderer a lazy slot registration stores: the loaded component
 * wrapped in the boundary the registration owns, with an observable loading
 * affordance rather than nothing.
 *
 * `React.lazy` is minted ONCE per call, outside the returned component, so a
 * per-props fallback never remints it and never remounts the loaded subtree.
 */
export function lazySlotRenderer<Props extends object>(
  slotKey: string,
  loader: () => Promise<LazySlotModule<Props>>,
  fallback?: LazySlotFallback<Props>,
): (props: Props) => React.ReactElement {
  const LazyRenderer = React.lazy(loader);
  // `undefined` means "no override"; an explicit `null` is an override that
  // must survive. `fallback ?? lazySlotFallback(key)` collapsed the two and
  // silently served the spinner to a caller that asked for nothing.
  const overridden = fallback !== undefined;
  const pending = (props: Props): React.ReactNode => {
    if (!overridden) return lazySlotFallback(slotKey);
    return typeof fallback === 'function'
      ? (fallback as (value: Props) => React.ReactNode)(props)
      : fallback;
  };
  return function LazySlot(props: Props): React.ReactElement {
    return (
      <React.Suspense fallback={pending(props)}>
        <LazyRenderer {...props} />
      </React.Suspense>
    );
  };
}
